import { useDroppable } from "@dnd-kit/core";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import { Header } from "@tanstack/react-table";
import classNames from "classnames";
import { KeyboardEvent, useCallback, useId } from "react";

import {
  COLUMN_RESIZE_LARGE_STEP,
  COLUMN_RESIZE_STEP,
  DEFAULT_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
} from "../column-width";
import { useTableContext } from "../context";
import { GRID_CELL_FOCUS_CLASSES } from "../grid-classes";
import { getColumnLabel } from "../utils";
import TableColumnHeader from "./cell";

export default function TableColumnHeaderWrapper({
  header,
}: {
  header: Header<unknown, unknown>;
}) {
  const { setNodeRef } = useDroppable({
    id: header.id,
  });
  const { columnWidths, onColumnResize, sortableFields, tableId, t } =
    useTableContext();
  const width = columnWidths[header.id];
  const currentWidth = width ?? DEFAULT_COLUMN_WIDTH;
  const columnName = getColumnLabel(header.column);
  const resizeInstructionsId = useId();
  const sortDirection = header.column.getIsSorted();
  const isSortable =
    !!header.column.getCanSort() &&
    !!sortableFields?.includes(header.column.id);
  const ariaSort = (() => {
    if (!isSortable) return undefined;
    if (sortDirection === "asc") return "ascending";
    if (sortDirection === "desc") return "descending";
    return "none";
  })();

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      let prevX = e.clientX;

      const onPointerMove = (ev: PointerEvent) => {
        const delta = ev.clientX - prevX;
        prevX = ev.clientX;
        onColumnResize(header.id, delta);
      };

      const onPointerUp = () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [header.id, onColumnResize],
  );

  const handleResizeKeyDown = (event: KeyboardEvent) => {
    const step = event.shiftKey ? COLUMN_RESIZE_LARGE_STEP : COLUMN_RESIZE_STEP;
    const deltas: Record<string, number> = {
      ArrowLeft: -step,
      ArrowRight: step,
      Home: MIN_COLUMN_WIDTH - currentWidth,
      End: MAX_COLUMN_WIDTH - currentWidth,
    };
    const delta = deltas[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    onColumnResize(header.id, delta);
  };

  return (
    <th
      id={`${tableId}-${header.id}`}
      ref={setNodeRef}
      scope="col"
      aria-sort={ariaSort}
      aria-labelledby={`${tableId}-${header.id}-label`}
      className={classNames(
        "h-target py-2 px-4 pr-target-min whitespace-nowrap text-ellipsis overflow-hidden relative border-r border-divider last:border-r-0",
        GRID_CELL_FOCUS_CLASSES,
      )}
      align="left"
      style={{ width }}
    >
      <TableColumnHeader
        key={header.id}
        header={header}
        isSortable={isSortable}
      />
      <div
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label={t("columnResizeName", {
          name: columnName,
          interpolation: NO_INTERPOLATION_ESCAPE,
        })}
        aria-valuenow={currentWidth}
        aria-valuemin={MIN_COLUMN_WIDTH}
        aria-valuemax={MAX_COLUMN_WIDTH}
        aria-valuetext={t("columnResizeValue", { width: currentWidth })}
        aria-describedby={resizeInstructionsId}
        onPointerDown={handlePointerDown}
        onKeyDown={handleResizeKeyDown}
        data-target-gap="column-resize"
        className="absolute top-0 right-0 h-full w-target-min cursor-col-resize group z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
      >
        <span id={resizeInstructionsId} className="sr-only">
          {t("columnResizeInstructions")}
        </span>
        <div className="absolute top-0 right-0 h-full w-0.5 group-hover:bg-primary group-active:bg-primary-hover transition-colors" />
      </div>
    </th>
  );
}
